import { Dispatch, SetStateAction } from 'react';
import { get } from 'lodash';
import * as R from 'remeda';
import { IntlShape } from 'react-intl';
import { SchemaDatapointEnumOption } from '@rossum/api-client/schemas';
import {
  IconClipboard,
  IconCopy,
  IconExternalLink,
  IconTrash,
} from '@rossum/ui/icons/tabler';
import { SvgIcon } from '@rossum/ui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridEditBooleanCell,
  GridEditInputCell,
  GridEditSingleSelectCell,
  GridRenderCellParams,
} from '@rossum/ui/x-data-grid-pro';
import { Queue } from '../../../types/queue';
import { FlatSchemaPropertyValue } from '../../../types/schema';
import { aggregationRow, text } from '../../constants';
import {
  getCategoryOptions,
  getTypeOptions,
  getUiConfigurationEditOptions,
  getUiConfigurationTypeOptions,
} from '../../data/options';
import { queuesOperators } from '../../queue-filtering/queueFilterOperators';
import { injectStylesToOperators } from '../../ui/dataGridStyles';
import { isNonEmptyObject } from '../getProcessRowUpdate';
import { DuplicateDialogParamsSetter } from '../hooks/useDuplicateFieldToQueues';
import { RemoveDialogParamsSetter } from '../hooks/useRemoveFromQueues';
import { optionsToHash } from '../rows/optionsToHash';
import { getFieldType } from '../rows/rows';
import { DataType, GridRowModel } from '../rows/rowTypes';
import { defaultValues } from './defaultValues';
import {
  canHaveRirFieldNames,
  isDatapoint,
  isDatapointInTable,
  isEnum,
  isFormula,
  isLookup,
  isNotButtonDatapoint,
  isNotTuple,
  isNotTupleNorSection,
  isNumberOrDate,
  isReasoning,
} from './editabilityConditions';
import { enumFormulaLabelsOperators } from './filterOperators';
import { renderFormatEditCell } from './format';
import {
  appendPercentSignToNumber,
  percentageToValue,
  validatePercentageRange,
  valueToPercentage,
} from './percentages';
import {
  countComparator,
  queuesComparator,
  rirFieldNamesComparator,
} from './sortComparators';
import {
  EnumAndFormulaCellValue,
  ExtraColDefProperties,
  GridColumns,
} from './types';
import { AggregationRenderCell } from './ui/AggregationRenderCell';
import { renderChipsCell } from './ui/ChipsCell';
import { renderMultipleEditAutocomplete } from './ui/EditCellAutocomplete';
import { renderEditCellWithValidation } from './ui/EditCellWithValidation';
import {
  renderBooleanCell,
  renderCellWithCount,
  renderFormulaCellWithCount,
  renderLookupCellWithCount,
  renderReasoningContextCell,
  renderReasoningPromptCell,
  renderSingleSelectCell,
} from './ui/renderCell';
import { StringWithIcon } from './ui/StringWithIcon';
import { renderWorkspaceWithQueues } from './ui/WorkspaceWithQueues';
import { validateNonEmptyString, validatePositiveInteger } from './validations';

const getFieldValue = (
  row: GridRowModel,
  colDef: GridColDef & ExtraColDefProperties
) => {
  if (!(row && colDef)) {
    return '';
  }

  const shouldFallbackToDefault =
    colDef.editable && colDef.editabilityCondition
      ? colDef.editabilityCondition(row.meta)
      : colDef.editable;

  const value: FlatSchemaPropertyValue =
    get(row, colDef.field) ??
    (shouldFallbackToDefault
      ? get(defaultValues, colDef.field, '')
      : text.cellNotEditable);

  return value;
};

const getFieldValueWithVariants: GridColumns[number]['valueGetter'] = (
  value,
  row
) => {
  if (!row) {
    return '';
  }

  if (row.meta.schema_id === aggregationRow) {
    return value;
  }

  if (
    'options' in row &&
    isNonEmptyObject(row.meta.metadata.optionsHashLabelMap)
  ) {
    const count = row.options.length;

    const hashKey = optionsToHash(row.options);

    return {
      label:
        count === 0
          ? 'Empty options'
          : row.meta.metadata.optionsHashLabelMap[hashKey],
      count,
    };
  }

  return { label: undefined, count: undefined };
};

const getFieldValueFormulaWithVariants: GridColumns[number]['valueGetter'] = (
  value,
  row
) => {
  if (!row) {
    return '';
  }
  if (row.meta.schema_id === aggregationRow) {
    return value;
  }

  if ('formula' in row && isNonEmptyObject(row.meta.metadata.formulaHashMap)) {
    const count = row.formula?.length;

    const hashKey = row.formula ?? '';

    return {
      label:
        count === 0
          ? 'Empty formula'
          : row.meta.metadata.formulaHashMap[hashKey],
      count,
    };
  }

  return { label: undefined, count: undefined };
};

const getFieldValuePromptWithVariants: GridColumns[number]['valueGetter'] = (
  value,
  row
) => {
  if (!row) {
    return '';
  }
  if (row.meta.schema_id === aggregationRow) {
    return value;
  }

  if ('prompt' in row && isNonEmptyObject(row.meta.metadata.promptHashMap)) {
    const count = row.prompt?.length ?? 0;

    const hashKey = row.prompt ?? '';

    return {
      label:
        count === 0 ? 'Empty prompt' : row.meta.metadata.promptHashMap[hashKey],
      count,
    };
  }

  return { label: undefined, count: undefined };
};

const getFieldValueMatchingWithVariants =
  (intl: IntlShape): GridColumns[number]['valueGetter'] =>
  (value, row) => {
    if (!row) {
      return '';
    }
    if (row.meta.schema_id === aggregationRow) {
      return value;
    }

    if (
      'matching' in row &&
      isNonEmptyObject(row.meta.metadata.matchingHashMap)
    ) {
      const hashKey = isNonEmptyObject(row.matching)
        ? JSON.stringify(row.matching)
        : '';
      const count = hashKey.length;

      return {
        label:
          count === 0
            ? intl.formatMessage({
                id: 'features.fieldManager.fieldDetail.columns.matching.emptyLookupConfig',
              })
            : row.meta.metadata.matchingHashMap[hashKey],
        count,
      };
    }

    return { label: undefined, count: undefined };
  };

type GetColumnsParams = {
  openRemoveModal?: RemoveDialogParamsSetter;
  setOptionsForEnumDialog?: Dispatch<
    SetStateAction<{
      options: SchemaDatapointEnumOption[];
      value: EnumAndFormulaCellValue;
    } | null>
  >;
  setFormulaForFormulaDrawer?: Dispatch<
    SetStateAction<{
      formula: string;
      value: EnumAndFormulaCellValue;
    } | null>
  >;
  setReasoningForDrawer?: Dispatch<
    SetStateAction<{
      prompt: string;
      context: string[];
      value: EnumAndFormulaCellValue;
    } | null>
  >;
  setLookupForDrawer?: Dispatch<
    SetStateAction<{
      matching: Record<string, unknown>;
      value: EnumAndFormulaCellValue;
    } | null>
  >;
  openDuplicateModal?: DuplicateDialogParamsSetter;
  setCopiedRow?: React.Dispatch<React.SetStateAction<GridRowModel | null>>;
  copiedRow?: GridRowModel | null;
  pasteFieldConfigurationToRows?: (rows: GridRowModel[]) => void;
  intl: IntlShape;
  setBatchEditError: Dispatch<SetStateAction<boolean>>;
};

// Remeda types were fucking up the original version in `getColumns` if used in-place, maybe try at a later time
function myCompact<T>(xs: (T | null | undefined)[]): T[] {
  return R.filter(xs, R.isTruthy);
}
type RenderCellFn = GridColDef['renderCell'];

const handleAggregationRowRenderCell =
  (renderCellFn: NonNullable<RenderCellFn>): RenderCellFn =>
  (...params: Parameters<typeof renderCellFn>) => {
    return params[0].id === aggregationRow ? (
      <AggregationRenderCell {...params[0]} />
    ) : (
      renderCellFn(...params)
    );
  };

const nonNavigableFieldTypes: DataType[] = ['simple-multivalue', 'tuple'];

export const getColumns = ({
  intl,
  openRemoveModal,
  setOptionsForEnumDialog,
  setFormulaForFormulaDrawer,
  setReasoningForDrawer,
  setLookupForDrawer,
  openDuplicateModal,
  setCopiedRow,
  copiedRow,
  pasteFieldConfigurationToRows,
  setBatchEditError,
}: GetColumnsParams) =>
  injectStylesToOperators(
    myCompact([
      {
        field: 'meta.schema_queues',
        filterOperators: queuesOperators,
        flex: 2.6,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.queues',
        }),
        minWidth: 260,
        renderCell: handleAggregationRowRenderCell(renderWorkspaceWithQueues),
        sortComparator: queuesComparator,
        valueGetter: (_, row, column) => {
          if (row.meta.schema_id === aggregationRow) {
            return getFieldValue(row, column);
          }
          const value = get(row, 'meta.schema_queues') as Queue[] | undefined;
          return (
            value?.flatMap(v =>
              v.id ? [{ id: String(v.id), name: v.name }] : []
            ) ?? []
          );
        },
      },
      {
        editable: true,
        field: 'label',
        flex: 2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.label',
        }),
        minWidth: 200,
        preProcessEditCellProps: validateNonEmptyString('Label', intl),
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams<GridRowModel, string>) => params.value
        ),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditInputCell,
        }),
      },
      {
        editable: true,
        field: 'description',
        flex: 2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.description',
        }),
        minWidth: 200,
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditInputCell,
        }),
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams<GridRowModel, string>) => params.value
        ),
      },
      {
        editable: false,
        field: 'category',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.category',
        }),
        minWidth: 120,
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams) => (
            <StringWithIcon value={params.formattedValue} />
          )
        ),
        type: 'singleSelect',
        valueGetter: (_, row, column) => {
          if (row?.category === 'multivalue') {
            const meta = row.meta as Record<string, unknown>;
            return meta['type'];
          }
          return getFieldValue(row, column);
        },
        valueOptions: getCategoryOptions(intl),
      },
      {
        editabilityCondition: isEnum,
        editable: false,
        field: 'options',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.options',
        }),
        minWidth: 120,
        sortComparator: countComparator,
        filterOperators: enumFormulaLabelsOperators,
        renderCell: handleAggregationRowRenderCell(
          renderCellWithCount(setOptionsForEnumDialog)
        ),
        type: 'singleSelect',
        valueGetter: getFieldValueWithVariants,
      },
      {
        editabilityCondition: isFormula,
        editable: false,
        field: 'formula',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.formula',
        }),
        minWidth: 120,
        sortComparator: countComparator,
        filterOperators: enumFormulaLabelsOperators,
        renderCell: handleAggregationRowRenderCell(
          renderFormulaCellWithCount(setFormulaForFormulaDrawer)
        ),
        type: 'singleSelect',
        valueGetter: getFieldValueFormulaWithVariants,
      },
      {
        editabilityCondition: isReasoning,
        editable: false,
        field: 'prompt',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.prompt',
        }),
        minWidth: 120,
        sortComparator: countComparator,
        filterOperators: enumFormulaLabelsOperators,
        renderCell: handleAggregationRowRenderCell(
          renderReasoningPromptCell(setReasoningForDrawer)
        ),
        type: 'singleSelect',
        valueGetter: getFieldValuePromptWithVariants,
      },
      {
        editabilityCondition: isReasoning,
        editable: false,
        field: 'context',
        flex: 1.6,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.context',
        }),
        minWidth: 160,
        renderCell: handleAggregationRowRenderCell(
          renderReasoningContextCell(setReasoningForDrawer)
        ),
        valueGetter: (_, row, column) => getFieldValue(row, column),
      },
      {
        editabilityCondition: isLookup,
        editable: false,
        field: 'matching',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.matching',
        }),
        minWidth: 120,
        sortComparator: countComparator,
        filterOperators: enumFormulaLabelsOperators,
        renderCell: handleAggregationRowRenderCell(
          renderLookupCellWithCount(setLookupForDrawer)
        ),
        type: 'singleSelect',
        valueGetter: getFieldValueMatchingWithVariants(intl),
      },
      {
        editabilityCondition: isDatapoint,
        editable: true,
        field: 'type',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.type',
        }),
        minWidth: 120,
        renderCell: handleAggregationRowRenderCell(renderSingleSelectCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditSingleSelectCell,
        }),
        type: 'singleSelect',
        valueGetter: (_, row, column) => getFieldValue(row, column),
        valueOptions: getTypeOptions(intl),
        valueSetter: (value, row) => {
          const rowWithNewValue = {
            ...row,
            type: value,
            ...(value === 'button'
              ? { rirFieldNames: null }
              : {
                  rirFieldNames:
                    get(row, 'rirFieldNames') ||
                    get(row.meta.original, 'rirFieldNames') ||
                    [],
                }),
            ...(value === 'enum'
              ? {
                  options:
                    get(row, 'options') ||
                    get(row.meta.original, 'options') ||
                    [],
                }
              : {}),
          };

          return {
            ...rowWithNewValue,
            meta: {
              ...rowWithNewValue.meta,
              type: getFieldType(rowWithNewValue),
            },
          };
        },
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotButtonDatapoint,
        editable: true,
        field: 'uiConfiguration.type',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.uiConfigurationType',
        }),
        minWidth: 120,
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams) => {
            return (
              <StringWithIcon value={params.formattedValue ?? params.value} />
            );
          }
        ),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditSingleSelectCell,
        }),
        type: 'singleSelect',
        valueGetter: (_, row, column) => getFieldValue(row, column),
        valueOptions: getUiConfigurationTypeOptions(intl),
        valueSetter: (value, row) => {
          const isComputedType =
            value === 'formula' || value === 'reasoning' || value === 'lookup';

          const rowWithNewValue = {
            ...row,
            uiConfiguration: {
              type: value,
              edit:
                'uiConfiguration' in row
                  ? (row.uiConfiguration?.edit ??
                    (isComputedType ? 'disabled' : 'enabled'))
                  : isComputedType
                    ? 'disabled'
                    : 'enabled',
            },
            ...(isComputedType
              ? {
                  rirFieldNames: null,
                  ...(value === 'formula' && {
                    formula: 'formula' in row ? row.formula : '',
                  }),
                  ...(value === 'reasoning' && {
                    prompt: 'prompt' in row ? row.prompt : '',
                    context: 'context' in row ? row.context : [],
                  }),
                  ...(value === 'lookup' && {
                    matching: 'matching' in row ? row.matching : {},
                  }),
                }
              : {
                  rirFieldNames:
                    get(row, 'rirFieldNames') ||
                    get(row.meta.original, 'rirFieldNames') ||
                    [],
                }),
          };

          return {
            ...rowWithNewValue,
            disablePrediction: value !== 'captured',
          } satisfies GridRowModel;
        },
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNumberOrDate,
        editable: true,
        field: 'format',
        flex: 2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.format',
        }),
        minWidth: 200,
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams<GridRowModel, string>) => params.value
        ),
        renderEditCell: renderFormatEditCell({ setBatchEditError }),
        valueGetter: (_, row, column) => getFieldValue(row, column),
        valueSetter: (value, row) => {
          const newValue =
            typeof value === 'object' && value !== null && 'value' in value
              ? value.value
              : value;

          return {
            ...row,
            // @ts-expect-error This is schema bullshit
            format: newValue,
          } satisfies GridRowModel;
        },
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotButtonDatapoint,
        editable: true,
        field: 'uiConfiguration.edit',
        flex: 1.6,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.uiConfigurationEdit',
        }),
        minWidth: 160,
        renderCell: handleAggregationRowRenderCell(renderSingleSelectCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditSingleSelectCell,
        }),
        type: 'singleSelect',
        valueGetter: (_, row, column) => {
          const value = getFieldValue(row, column);
          // We've changed the formula edit value could only be disabled or enabled so if there was any formula with "enabled_without_warning"
          // we need to sanitize such value
          const isFieldFormula =
            row &&
            'uiConfiguration' in row &&
            row.uiConfiguration?.type === 'formula';

          const adjustedFormulaEditabilityValue =
            value === 'enabled_without_warning' ? 'enabled' : value;

          return isFieldFormula ? adjustedFormulaEditabilityValue : value;
        },
        valueOptions: params => getUiConfigurationEditOptions(intl, params.row),
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotButtonDatapoint,
        editable: true,
        field: 'scoreThreshold',
        flex: 1.4,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.scoreThreshold',
        }),
        minWidth: 140,
        preProcessEditCellProps: validatePercentageRange(
          'Score threshold',
          intl
        ),
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams<GridRowModel, string>) =>
            params.formattedValue ?? params.value
        ),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditInputCell,
        }),
        type: 'number',
        valueFormatter: appendPercentSignToNumber,
        valueGetter: (_, row, column) => {
          const value = getFieldValue(row, column);

          return valueToPercentage(value);
        },
        valueSetter: (value, row) => {
          const scoreThreshold = percentageToValue(value);

          return {
            ...row,
            // @ts-expect-error This is schema bullshit
            scoreThreshold,
          } satisfies GridRowModel;
        },
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotButtonDatapoint,
        editable: true,
        field: 'constraints.required',
        flex: 1,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.constraintsRequired',
        }),
        minWidth: 100,
        renderCell: handleAggregationRowRenderCell(renderBooleanCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditBooleanCell,
        }),
        type: 'boolean',
        valueGetter: (_, row, column) => getFieldValue(row, column),
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotTuple,
        editable: true,
        field: 'hidden',
        flex: 1,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.hidden',
        }),
        minWidth: 100,
        renderCell: handleAggregationRowRenderCell(renderBooleanCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditBooleanCell,
        }),
        type: 'boolean',
        valueGetter: (_, row, column) => {
          if (!(row && column)) {
            return '';
          }
          if (row.meta.schema_id === aggregationRow) {
            return getFieldValue(row, column);
          }

          return !getFieldValue(row, column);
        },
        valueSetter: (value, row) =>
          ({ ...row, hidden: !value }) satisfies GridRowModel,
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotTupleNorSection,
        editable: true,
        field: 'canExport',
        flex: 1,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.canExport',
        }),
        minWidth: 100,
        renderCell: handleAggregationRowRenderCell(renderBooleanCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditBooleanCell,
        }),
        type: 'boolean',
        valueGetter: (_, row, column) => getFieldValue(row, column),
      } satisfies GridColumns[number],
      {
        editabilityCondition: canHaveRirFieldNames,
        editable: true,
        field: 'rirFieldNames',
        flex: 2.4,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.rirFieldNames',
        }),
        minWidth: 240,
        renderCell: handleAggregationRowRenderCell(renderChipsCell),
        renderEditCell: renderMultipleEditAutocomplete({ setBatchEditError }),
        sortComparator: rirFieldNamesComparator,
        valueGetter: (_, row, column) => getFieldValue(row, column),
        valueSetter: (value, row) => {
          const newValue =
            R.isDeepEqual(value, []) &&
            ('rirFieldNames' in row.meta.original &&
              row.meta.original.rirFieldNames) === null
              ? null
              : value;

          return {
            ...row,
            // @ts-expect-error This is schema bullshit
            rirFieldNames: newValue,
          } satisfies GridRowModel;
        },
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotTupleNorSection,
        editable: true,
        field: 'disablePrediction',
        flex: 1.6,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.disablePrediction',
        }),
        minWidth: 160,
        renderCell: handleAggregationRowRenderCell(renderBooleanCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditBooleanCell,
        }),
        type: 'boolean',
        valueGetter: (_, row, column) => getFieldValue(row, column),
      } satisfies GridColumns[number],
      {
        editabilityCondition: isDatapointInTable,
        editable: true,
        field: 'canCollapse',
        flex: 1.2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.canCollapse',
        }),
        minWidth: 120,
        renderCell: handleAggregationRowRenderCell(renderBooleanCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditBooleanCell,
        }),
        type: 'boolean',
        valueGetter: (_, row, column) => getFieldValue(row, column),
      } satisfies GridColumns[number],
      {
        editabilityCondition: isNotButtonDatapoint,
        editable: true,
        field: 'defaultValue',
        flex: 2,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.defaultValue',
        }),
        minWidth: 200,
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams<GridRowModel, string>) => params.value
        ),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditInputCell,
        }),
        valueGetter: (_, row, column) => getFieldValue(row, column),
        valueSetter: (value, row) => {
          const newValue =
            value === '' &&
            ('defaultValue' in row.meta.original &&
              row.meta.original.defaultValue) === null
              ? null
              : value;

          return {
            ...row,
            defaultValue: newValue,
          };
        },
      } satisfies GridColumns[number],
      {
        editabilityCondition: isDatapointInTable,
        editable: true,
        field: 'width',
        flex: 0.8,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.width',
        }),
        minWidth: 80,
        preProcessEditCellProps: validatePositiveInteger('Width', intl),
        renderCell: handleAggregationRowRenderCell(
          (params: GridRenderCellParams<GridRowModel, string>) => params.value
        ),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditInputCell,
        }),
        type: 'number',
        valueGetter: (_, row, column) => getFieldValue(row, column),
        valueSetter: (value, row) =>
          ({
            ...row,
            // @ts-expect-error This is schema bullshit
            width: value === undefined ? null : value,
          }) satisfies GridRowModel,
      } satisfies GridColumns[number],
      {
        editabilityCondition: isDatapointInTable,
        editable: true,
        field: 'stretch',
        flex: 1,
        headerName: intl.formatMessage({
          id: 'features.fieldManager.fieldDetail.columns.headerName.stretch',
        }),
        minWidth: 100,
        renderCell: handleAggregationRowRenderCell(renderBooleanCell),
        renderEditCell: renderEditCellWithValidation({
          setBatchEditError,
          Component: GridEditBooleanCell,
        }),
        type: 'boolean',
        valueGetter: (_, row, column) => getFieldValue(row, column),
      } satisfies GridColumns[number],
      (openRemoveModal ||
        openDuplicateModal ||
        setCopiedRow ||
        pasteFieldConfigurationToRows) && {
        field: 'actions',

        getActions: ({ row, id }) => {
          if (!row || id === aggregationRow) {
            return [];
          }
          const firstQueueId = row.meta.schema_queues?.[0]?.id;

          const canNavigateToField =
            firstQueueId && !nonNavigableFieldTypes.includes(row.meta.type);

          const actions = [
            canNavigateToField ? (
              <GridActionsCellItem
                key={row.id}
                icon={
                  <SvgIcon fontSize="medium">
                    <IconExternalLink />
                  </SvgIcon>
                }
                label={intl.formatMessage({
                  id: 'features.fieldManager.fieldDetail.columns.actions.open',
                })}
                onClick={e => {
                  const url = `/queues/${firstQueueId}/settings/fields/${row.id}`;
                  e.stopPropagation();
                  window.open(url, '_blank');
                }}
                data-cy="fm-open-fields-to-capture"
                showInMenu
              />
            ) : (
              []
            ),

            openRemoveModal ? (
              <GridActionsCellItem
                key={row.id}
                icon={
                  <SvgIcon fontSize="medium">
                    <IconTrash />
                  </SvgIcon>
                }
                label={intl.formatMessage({
                  id: 'features.fieldManager.fieldDetail.columns.actions.remove',
                })}
                onClick={() => openRemoveModal({ selectedRows: [row] })}
                data-cy="fm-remove-field-action"
                showInMenu
              />
            ) : (
              []
            ),

            // Allow to duplicate only single value fields
            openDuplicateModal && row.category === 'datapoint' ? (
              <GridActionsCellItem
                key={row.id}
                icon={
                  <SvgIcon fontSize="medium">
                    <IconCopy />
                  </SvgIcon>
                }
                label={intl.formatMessage({
                  id: 'features.fieldManager.fieldDetail.columns.actions.duplicate',
                })}
                onClick={() => openDuplicateModal({ selectedRows: [row] })}
                data-cy="fm-duplicate-field-action"
                showInMenu
              />
            ) : (
              []
            ),
            // Allow to copy only single value fields
            setCopiedRow && row.category === 'datapoint' ? (
              <GridActionsCellItem
                key={row.id}
                icon={
                  <SvgIcon fontSize="medium">
                    <IconCopy />
                  </SvgIcon>
                }
                label={intl.formatMessage({
                  id: 'features.fieldManager.fieldDetail.columns.actions.copyToClipBoard',
                })}
                onClick={() => setCopiedRow(row)}
                data-cy="fm-copy-field-action"
                showInMenu
              />
            ) : (
              []
            ),
            // Allow to paste only to a single value fields and NOT to the same row which was copied
            copiedRow &&
            copiedRow.meta.schema_id !== row.meta.schema_id &&
            pasteFieldConfigurationToRows &&
            row.category === 'datapoint' ? (
              <GridActionsCellItem
                key={row.id}
                icon={
                  <SvgIcon fontSize="medium">
                    <IconClipboard />
                  </SvgIcon>
                }
                label={intl.formatMessage({
                  id: 'features.fieldManager.fieldDetail.columns.actions.paste',
                })}
                onClick={() => pasteFieldConfigurationToRows([row])}
                data-cy="fm-paste-field-action"
                showInMenu
              />
            ) : (
              []
            ),
          ].flatMap(action => action);

          return actions;
        },
        resizable: false,
        type: 'actions',
        width: 50,
      },
    ])
  );
